import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';

import { addDays, lastCompleteDay } from '../ProductPerformance/model';

import { LIVE_CARDS, liveBenchmark } from './live';
import type { FlashSnapshot } from './types';

type SourceRow = Record<string, unknown>;
const object = (v: unknown): v is SourceRow => !!v && typeof v === 'object' && !Array.isArray(v);
const strings = (v: unknown): v is Array<string> => Array.isArray(v) && v.every((x) => typeof x === 'string');
const optionalStrings = (v: unknown) => v === null || strings(v);
const text = (v: unknown) => v === null || typeof v === 'string';
const number = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
const integer = (v: unknown) =>
  (typeof v === 'number' || (typeof v === 'string' && /^\d+$/.test(v))) && Number.isSafeInteger(Number(v));
export const validDay = (v: unknown): v is string =>
  typeof v === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(v) &&
  v.slice(0, 4) !== '0000' &&
  Number.isFinite(Date.parse(v)) &&
  new Date(v).toISOString().slice(0, 10) === v;
export function benchmarkRequest(clock: Date): { asOf: string; filters: LensFilterValues } {
  const to = lastCompleteDay(clock);
  return { asOf: addDays(to, 1), filters: { lens_period: { from: '2026-01-01', to } } };
}
function validRecord(id: string, row: SourceRow): boolean {
  if (id === 'freshness') {
    return (
      typeof row.source === 'string' &&
      ['impressions', 'views', 'sales'].includes(row.source) &&
      validDay(row.fact_date)
    );
  }
  if (typeof row.offer_bk !== 'string' || !row.offer_bk) {
    return false;
  }
  if (id === 'schedules') {
    return (
      typeof row.id === 'string' &&
      !!row.id &&
      validDay(row.start_date) &&
      validDay(row.end_date) &&
      row.end_date >= row.start_date &&
      ['name', 'vendor_name', 'country', 'destination', 'owner', 'campaign_type'].every((key) => text(row[key])) &&
      ['product_bk', 'model', 'dynamic_type'].every((key) => typeof row[key] === 'string') &&
      typeof row.product_mapping_basis === 'string' &&
      ['direct_offer', 'linked_service'].includes(row.product_mapping_basis) &&
      strings(row.product_values) &&
      strings(row.product_bookable_offer_bks)
    );
  }
  if (id === 'dimensions') {
    return ['pcm_values', 'territory_values', 'offer_owner_values'].every((key) => optionalStrings(row[key]));
  }
  if (id === 'identities') {
    return strings(row.identity_values);
  }
  return (
    validDay(row.date) &&
    ['views', 'packages', 'margin', ...(id === 'daily' ? ['impressions'] : [])].every((key) => number(row[key])) &&
    ['views_observed', 'sales_observed', ...(id === 'daily' ? ['impression_observed'] : [])].every(
      (key) => typeof row[key] === 'boolean',
    ) &&
    (id !== 'markets' || typeof row.market === 'string')
  );
}
/** The source population, not the returned page size, certifies a complete batch set. */
export function completeBenchmarkRows(card: LensCardData): Array<SourceRow> {
  const id = card.cardId;
  if (!LIVE_CARDS.includes(id) || !['ready', 'empty'].includes(card.status) || card.truncated) {
    throw new Error(`${id}: complete evidence is required.`);
  }
  const rows = card.rows ?? [];
  if (!rows.length) {
    if (card.status !== 'empty') {
      throw new Error(`${id}: missing batch population.`);
    }
    return [];
  }
  const indices = new Set<number>();
  let expected: number | undefined;
  const decoded: Array<SourceRow> = [];
  for (const batch of rows) {
    if (![batch.batch, batch.record_count, batch.source_count].every(integer))
      throw new Error(`${id}: invalid batch metadata.`);
    const index = Number(batch.batch),
      count = Number(batch.record_count),
      total = Number(batch.source_count);
    if (
      !Number.isSafeInteger(index) ||
      index < 0 ||
      indices.has(index) ||
      !Number.isSafeInteger(count) ||
      count < 1 ||
      count > 1000 ||
      !Number.isSafeInteger(total) ||
      total < count ||
      (expected !== undefined && expected !== total)
    ) {
      throw new Error(`${id}: invalid batch identity or population.`);
    }
    indices.add(index);
    expected = total;
    if (typeof batch.payload !== 'string') {
      throw new Error(`${id}: invalid evidence payload.`);
    }
    const parsed: unknown = JSON.parse(batch.payload);
    if (!Array.isArray(parsed) || parsed.length !== count) {
      throw new Error(`${id}: evidence count does not match.`);
    }
    for (const value of parsed) {
      if (!object(value) || !validRecord(id, value)) {
        throw new Error(`${id}: invalid source record.`);
      }
      decoded.push(value);
    }
  }
  if (expected !== decoded.length || Array.from(indices).some((index) => index >= indices.size)) {
    throw new Error(`${id}: incomplete evidence population.`);
  }
  // Duplicate source keys would change campaign-day denominators or overwrite mappings.
  const keys = decoded.map((row) =>
    JSON.stringify(
      id === 'schedules'
        ? [row.id]
        : id === 'freshness'
          ? [row.source]
          : id === 'daily'
            ? [row.offer_bk, row.date]
            : id === 'markets'
              ? [row.offer_bk, row.date, row.market]
              : [row.offer_bk],
    ),
  );
  if (new Set(keys).size !== keys.length) {
    throw new Error(`${id}: duplicate source identity.`);
  }
  return decoded;
}
export function adaptBenchmark(
  query: LensDataReadiness,
  asOf: string,
): { ready: boolean; data?: FlashSnapshot; error?: Error } {
  if (!lensDataReady(query, LIVE_CARDS) || !validDay(asOf)) {
    return { ready: false };
  }
  try {
    for (const id of LIVE_CARDS) {
      completeBenchmarkRows(query.cards.find((c) => c.cardId === id));
    }
    const data = liveBenchmark([...query.cards], asOf);
    if (!validDay(data.meta.commonFactDate)) {
      throw new Error('All source freshness dates are required.');
    }
    return { ready: true, data };
  } catch (error) {
    return { ready: false, error: error instanceof Error ? error : new Error('Invalid benchmark evidence.') };
  }
}
export function exportBenchmark(
  ready: boolean,
  currentIdentity: string,
  renderedIdentity: string,
  csv: () => string,
): string {
  if (!ready || currentIdentity !== renderedIdentity) {
    throw new Error('Wait for complete evidence for the current filters before exporting.');
  }
  return csv();
}
